import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OtpPanel } from "@/components/common/OtpPanel";
import { FileUpload } from "@/components/common/FileUpload";
import { TextInput, SelectInput, Stepper, WizardNav, StatusView } from "@/components/common/onboardingUi";
import { AuthShell, AuthBrandHeader } from "@/components/common/AuthShell";

const STEPS = ["Personal", "KYC", "Vehicle", "Vehicle Docs", "Bank", "Preferences", "Review"];
const empty = { account: {}, personal: {}, kyc: {}, vehicle: {}, documents: {}, bank: {}, preferences: {} };

export default function DeliveryOnboarding() {
  const nav = useNavigate();
  const { user, login, refreshUser } = useAuth();
  const [phase, setPhase] = useState("loading");
  const [status, setStatus] = useState("draft");
  const [reason, setReason] = useState("");
  const [idx, setIdx] = useState(0);
  const [d, setD] = useState(empty);
  const [acc, setAcc] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  const boot = async () => {
    if (!user || user.role !== "delivery") { setPhase("account"); return; }
    try {
      const { data } = await api.get("/onboarding");
      const st = data.onboarding_status || "draft";
      setStatus(st); setReason(data.review_reason || "");
      if (data.onboarding && Object.keys(data.onboarding).length) setD({ ...empty, ...data.onboarding });
      if (["submitted", "under_review", "approved", "suspended"].includes(st)) setPhase("status");
      else setPhase("wizard");
    } catch { setPhase("wizard"); }
  };
  useEffect(() => { boot(); /* eslint-disable-next-line */ }, [user]);

  const sec = (s) => (k, v) => setD((p) => ({ ...p, [s]: { ...p[s], [k]: v } }));
  const pf = sec("personal"), kf = sec("kyc"), vf = sec("vehicle"), bf = sec("bank"), prf = sec("preferences");
  const setDoc = (k) => (rec) => setD((p) => ({ ...p, documents: { ...p.documents, [k]: rec } }));

  const startAccount = () => {
    if (!acc.name.trim()) return toast.error("Enter your name");
    if (acc.phone.length < 10) return toast.error("Enter valid mobile");
    if (!/^\S+@\S+\.\S+$/.test(acc.email)) return toast.error("Enter valid email");
    setPhase("otp");
  };
  const onVerified = (data) => { login(data.token, data.user); setD((p) => ({ ...p, account: acc, personal: { ...p.personal, name: acc.name, email: acc.email, phone: acc.phone } })); toast.success("Account verified"); setPhase("wizard"); setIdx(0); };

  const save = async (submit) => {
    setSubmitting(true);
    try {
      const payload = { ...d, account: d.account?.name ? d.account : acc };
      const { data } = await api.post("/onboarding/delivery", { data: payload, submit });
      setStatus(data.onboarding_status);
      if (submit) { await refreshUser(); toast.success("Submitted for verification"); setPhase("status"); }
      else toast.success("Draft saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  if (phase === "loading") return <div className="min-h-screen bg-surface flex items-center justify-center"><Loader2 className="animate-spin text-coral" size={26} /></div>;

  return (
    <AuthShell align="start" maxWidth="600px">
      <div className="pb-28" data-testid="delivery-onboarding">
        <AuthBrandHeader />
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => nav("/login")} className="w-9 h-9 flex items-center justify-center text-ink hover:text-coral transition-colors tap"><ArrowLeft size={22} /></button>
          <h1 className="font-display font-bold text-lg text-ink">Register as Delivery Partner</h1>
        </div>

      {phase === "status" && <StatusView status={status} reason={reason} onResubmit={() => { setPhase("wizard"); setIdx(0); }} onGoDashboard={() => nav("/rider")} />}

      {phase === "account" && (
        <div className="bg-white border border-border rounded-2xl p-5 space-y-3">
          <h2 className="font-display font-bold text-lg text-ink">Account details</h2>
          <TextInput label="Full name" required value={acc.name} onChange={(v) => setAcc((p) => ({ ...p, name: v }))} testid="ob-d-name" />
          <div><label className="text-xs font-semibold text-ink-600">Mobile number *</label><div className="flex mt-1"><span className="bg-surface border border-border border-r-0 rounded-l-xl px-3 py-2.5 text-sm text-ink-600">+91</span><input value={acc.phone} onChange={(e) => setAcc((p) => ({ ...p, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))} placeholder="10-digit number" className="flex-1 min-w-0 bg-surface rounded-r-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 ring-coral" data-testid="ob-d-phone" /></div></div>
          <TextInput label="Email" required value={acc.email} onChange={(v) => setAcc((p) => ({ ...p, email: v }))} testid="ob-d-email" />
          <button onClick={startAccount} className="w-full bg-coral text-white font-bold py-3 rounded-xl" data-testid="ob-d-account-continue">Continue</button>
        </div>
      )}

      {phase === "otp" && <div className="bg-white border border-border rounded-2xl p-5"><OtpPanel phone={acc.phone} email={acc.email} role="delivery" title="Verify your account" extra={{ name: acc.name }} onBack={() => setPhase("account")} onVerified={onVerified} /></div>}

      {phase === "wizard" && (
        <>
          <Stepper steps={STEPS} index={idx} />
          <div className="bg-white border border-border rounded-2xl p-4 mt-3 space-y-3">
            <h2 className="font-display font-bold text-base text-ink">{STEPS[idx]}</h2>
            {idx === 0 && <>
              <TextInput label="Full name" required value={d.personal.name || acc.name} onChange={(v) => pf("name", v)} testid="ob-d-p-name" />
              <TextInput label="Date of birth" value={d.personal.dob} onChange={(v) => pf("dob", v)} placeholder="DD/MM/YYYY" />
              <TextInput label="Address" value={d.personal.address} onChange={(v) => pf("address", v)} />
              <div className="flex gap-2"><TextInput half label="City" value={d.personal.city} onChange={(v) => pf("city", v)} /><TextInput half label="Pincode" value={d.personal.pincode} onChange={(v) => pf("pincode", v)} /></div>
              <TextInput label="Emergency contact" value={d.personal.emergency} onChange={(v) => pf("emergency", v)} />
            </>}
            {idx === 1 && <>
              <p className="text-xs text-ink-600">Provide applicable identity documents.</p>
              <TextInput label="PAN" value={d.kyc.pan} onChange={(v) => kf("pan", v)} testid="ob-d-pan" />
              <TextInput label="Government ID (Aadhaar/Voter/DL no.)" value={d.kyc.gov_id} onChange={(v) => kf("gov_id", v)} />
            </>}
            {idx === 2 && <>
              <SelectInput label="Vehicle type" value={d.vehicle.type} onChange={(v) => vf("type", v)} options={["Bike", "Scooter", "Bicycle", "Other"]} testid="ob-d-vtype" />
              <TextInput label="Vehicle number" value={d.vehicle.number} onChange={(v) => vf("number", v)} testid="ob-d-vnum" />
              <div className="flex gap-2"><TextInput half label="Make" value={d.vehicle.make} onChange={(v) => vf("make", v)} /><TextInput half label="Model" value={d.vehicle.model} onChange={(v) => vf("model", v)} /></div>
              <TextInput label="Year" value={d.vehicle.year} onChange={(v) => vf("year", v)} />
            </>}
            {idx === 3 && <div className="space-y-2">
              <FileUpload purpose="kyc:driving_licence" label="Driving licence" value={d.documents.dl} onChange={setDoc("dl")} />
              <FileUpload purpose="kyc:rc" label="RC (registration certificate)" value={d.documents.rc} onChange={setDoc("rc")} />
              <FileUpload purpose="kyc:insurance" label="Insurance" value={d.documents.insurance} onChange={setDoc("insurance")} />
              <FileUpload purpose="kyc:puc" label="PUC (if applicable)" value={d.documents.puc} onChange={setDoc("puc")} />
              <FileUpload purpose="kyc:id_proof" label="Identity proof" value={d.documents.id_proof} onChange={setDoc("id_proof")} />
            </div>}
            {idx === 4 && <>
              <TextInput label="Account holder name" value={d.bank.holder} onChange={(v) => bf("holder", v)} testid="ob-d-bank-holder" />
              <TextInput label="Bank name" value={d.bank.bank} onChange={(v) => bf("bank", v)} />
              <TextInput label="Account number" value={d.bank.account} onChange={(v) => bf("account", v)} />
              <TextInput label="IFSC" value={d.bank.ifsc} onChange={(v) => bf("ifsc", v)} />
              <TextInput label="UPI ID (optional)" value={d.bank.upi} onChange={(v) => bf("upi", v)} />
            </>}
            {idx === 5 && <>
              <TextInput label="Preferred working area" value={d.preferences.area} onChange={(v) => prf("area", v)} />
              <TextInput label="Working hours" value={d.preferences.hours} onChange={(v) => prf("hours", v)} placeholder="10 AM - 8 PM" />
              <TextInput label="Preferred language" value={d.preferences.language} onChange={(v) => prf("language", v)} />
            </>}
            {idx === 6 && <div className="space-y-2 text-sm">
              <Review label="Name" value={d.personal.name || acc.name} />
              <Review label="Vehicle" value={`${d.vehicle.type || ""} ${d.vehicle.number || ""}`} />
              <Review label="PAN" value={d.kyc.pan} />
              <Review label="Documents uploaded" value={Object.values(d.documents).filter(Boolean).length + " file(s)"} />
              <p className="text-xs text-ink-600 pt-2">Submit for verification. You won't receive delivery assignments until BiteGoFood approves your account.</p>
            </div>}
          </div>
          <WizardNav index={idx} total={STEPS.length} onBack={() => (idx === 0 ? nav("/login") : setIdx(idx - 1))} onNext={() => setIdx(Math.min(STEPS.length - 1, idx + 1))} onSaveDraft={() => save(false)} onSubmit={() => save(true)} submitting={submitting} />
        </>
      )}
      </div>
    </AuthShell>
  );
}

function Review({ label, value }) {
  return <div className="flex justify-between border-b border-border py-1.5"><span className="text-ink-600">{label}</span><span className="font-semibold text-ink text-right">{value || "—"}</span></div>;
}
